import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { calculateTokenExpiry } from '@/lib/utils';
import { restoreSession, logoutUser, businessSignup, businessVerify, businessSignIn } from './authThunks';
import type { AuthState } from './authTypes';
import type { RootState } from '@/store/root-state';
import type { StoredSessionData } from '@/services/auth/types';
import { isCognitoSystemSuperAdmin } from '@/services/auth/cognito-id-token';
import { getBusinessAuthMessage } from './businessAuthMessages';

const initialState: AuthState = {
  isAuthenticated: false,
  step: 'email',
  email: null,
  password: '',
  role: '',
  department: '',
  userSub: null,
  codeDestination: null,
  accessToken: null,
  refreshToken: null,
  idToken: null,
  tokenExpiresAt: null,
  userId: null,
  isLoading: false,
  error: null,
  isRestoringSession: true,
  authUserType: null,
  authRole: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setEmailData: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setPasswordData: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    setRole: (state, action: PayloadAction<string>) => {
      state.role = action.payload;
    },
    setDepartment: (state, action: PayloadAction<string>) => {
      state.department = action.payload;
    },
    logout: () => {
      // Clear all auth-related localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('tokenExpiresAt');
      localStorage.removeItem('userId');

      // Reset to initial state
      return { ...initialState };
    },
    clearError: (state) => {
      state.error = null;
    },
    resetToRegistrationStep: (state) => {
      state.step = 'registration';
      state.error = null;
    },
    setVerificationStep: (state, action: PayloadAction<string>) => {
      state.step = 'verification';
      state.email = action.payload;
      state.error = null;
    },
    syncRefreshedSession: (state, action: PayloadAction<StoredSessionData>) => {
      const session = action.payload;
      state.accessToken = session.accessToken;
      state.refreshToken = session.refreshToken;
      state.idToken = session.idToken;
      state.tokenExpiresAt = session.expiresAt;
      state.userId = session.userId;
      state.email = session.email;
      state.authUserType = session.authUserType;
      state.authRole = session.authRole;
      state.isAuthenticated = Boolean(session.accessToken && session.userId);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(restoreSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isRestoringSession = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRestoringSession = false;

        if (action.payload && action.payload.accessToken) {
          const { accessToken, email, refreshToken, idToken, expiresAt, authRole, authUserType, userId } =
            action.payload;

          state.accessToken = accessToken;
          state.refreshToken = refreshToken;
          state.idToken = idToken;
          state.tokenExpiresAt = expiresAt;
          state.authUserType = authUserType || null;
          state.authRole = authRole || null;
          state.userId = userId;
          state.email = email;
          state.step = 'authenticated';
          state.isAuthenticated = true;
        }
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.isLoading = false;
        state.isRestoringSession = false;
        state.error = action.error.message || 'Failed to restore session';
      });

    // Handle logoutUser
    builder.addCase(logoutUser.fulfilled, () => {
      return { ...initialState, isRestoringSession: false };
    });

    // Handle businessSignup
    builder
      .addCase(businessSignup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(businessSignup.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success && action.payload.data?.nextStep === 'CHECK_EMAIL') {
          state.step = 'verification';
          state.userSub = null;
          state.codeDestination = null;
        } else {
          state.error = getBusinessAuthMessage(action.payload.message, 'Registration failed');
        }
      })
      .addCase(businessSignup.rejected, (state, action) => {
        state.isLoading = false;
        state.step = 'registration';
        const payload = action.payload as { message?: string } | undefined;
        state.error = getBusinessAuthMessage(payload?.message, action.error.message || 'Failed to register');
      });

    // Handle businessVerify
    builder
      .addCase(businessVerify.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(businessVerify.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success && action.payload.data?.nextStep === 'SIGN_IN') {
          state.step = 'complete';
        } else {
          state.error = getBusinessAuthMessage(action.payload.message, 'Verification failed');
        }
      })
      .addCase(businessVerify.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error = getBusinessAuthMessage(payload?.message, action.error.message || 'Failed to verify code');
      });

    // Handle businessSignIn
    builder
      .addCase(businessSignIn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(businessSignIn.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success && action.payload.tokens) {
          state.isAuthenticated = true;
          state.isRestoringSession = false;
          state.step = 'authenticated';
          state.accessToken = action.payload.tokens.accessToken;
          state.refreshToken = action.payload.tokens.refreshToken;
          state.idToken = action.payload.tokens.idToken;
          state.tokenExpiresAt = calculateTokenExpiry(action.payload.tokens.expiresIn);
          state.userId = action.payload.userId || null;
          state.authUserType = action.payload.authUserType;
          state.authRole = action.payload.authRole;
        } else {
          state.error = action.payload.message || 'Login failed';
        }
      })
      .addCase(businessSignIn.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error = getBusinessAuthMessage(payload?.message, action.error.message || 'Failed to login');
      });
  },
});

export const {
  setEmailData,
  setPasswordData,
  setRole,
  setDepartment,
  logout,
  clearError,
  resetToRegistrationStep,
  setVerificationStep,
  syncRefreshedSession,
} = authSlice.actions;

//Memoised selectors
const selectAuthState = (state: RootState) => state.auth;

export const selectIsSuperAdmin = createSelector([selectAuthState], (auth) =>
  isCognitoSystemSuperAdmin(auth.authRole, auth.authUserType),
);

export default authSlice.reducer;
